import React, { useState, useMemo } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiGlobe, FiBarChart2, FiList } from 'react-icons/fi';
import { CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';
import ContractDrillDownPanel from './ContractDrillDownPanel';
import { countryName } from './countryNames';

const COLORS = ['#3B82F6','#10B981','#6366F1','#F59E0B','#EF4444','#A855F7','#06B6D4','#F97316','#EC4899','#14B8A6'];

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`;

const METRICS = [
  { key: 'contract_count',   label: 'Count',    fmt: v => v },
  { key: 'total_value',      label: 'Value $',  fmt: fmtM },
  { key: 'contract_weight',  label: 'Weight',   fmt: fmtW },
];

const ContractCountryChart = ({ data, loading, error, baseParams }) => {
  const [view,       setView]       = useState('bar');
  const [metric,     setMetric]     = useState('contract_count');
  const [drillOpen,  setDrillOpen]  = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillExtra, setDrillExtra] = useState({});
  const [tableSort,  setTableSort]  = useState({ key: 'contract_count', dir: 'desc' });

  const handleTableSort = (key) => {
    setTableSort(prev =>
      prev.key === key
        ? { ...prev, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    );
  };

  const tableRows = useMemo(() => {
    const sorted = [...(data || [])].sort((a, b) => {
      const diff = (b[tableSort.key] || 0) - (a[tableSort.key] || 0);
      return tableSort.dir === 'desc' ? diff : -diff;
    });
    return sorted;
  }, [data, tableSort]);

  const items  = useMemo(() => [...(data || [])].sort((a, b) => (b[metric] || 0) - (a[metric] || 0)), [data, metric]);
  const maxVal = useMemo(() => Math.max(...items.map(r => r[metric] || 0), 1), [items, metric]);

  const activeMetric = METRICS.find(m => m.key === metric) || METRICS[0];

  const barOption = useMemo(() => ({
    textStyle: { fontFamily: CHART_FONT.fontFamily },
    grid: { left: 8, right: 84, top: 4, bottom: 4, containLabel: true },
    tooltip: {
      ...CHART_TOOLTIP,
      formatter: (p) => {
        const row = items[p.dataIndex];
        if (!row) return '';
        return [
          `<b>${escapeHtml(countryName(row.country))}</b> <span style="color:#9CA3AF">${escapeHtml(row.country)}</span>`,
          `${activeMetric.label}: <b>${escapeHtml(String(activeMetric.fmt(row[metric] || 0)))}</b>`,
          `<span style="color:#2563EB;font-weight:600">${row.contract_count}C · ${row.addendum_count || 0}A</span>`,
          escapeHtml(fmtW(row.contract_weight))
        ].join('<br/>');
      }
    },
    xAxis: { type: 'value', show: false, max: maxVal },
    yAxis: {
      type: 'category',
      inverse: true,
      data: items.map(r => r.country),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...CHART_FONT,
        formatter: (value, index) => `{rank|${index + 1}} {name|${countryName(value)}}\n{iso|${value}}`,
        rich: {
          rank: { color: '#9CA3AF', fontSize: 11, width: 16, align: 'right' },
          name: { color: '#1F2937', fontWeight: 'bold', fontSize: 11, align: 'left' },
          iso:  { color: '#9CA3AF', fontSize: 10, padding: [2, 0, 0, 20], align: 'left' }
        }
      }
    },
    series: [{
      type: 'bar',
      barWidth: 20,
      cursor: 'pointer',
      showBackground: true,
      backgroundStyle: { color: '#F3F4F6', borderRadius: 10 },
      itemStyle: { borderRadius: 10, opacity: 0.8 },
      label: {
        show: true,
        position: 'right',
        fontSize: 11,
        fontWeight: 'bold',
        color: 'inherit',
        formatter: (p) => String(activeMetric.fmt(items[p.dataIndex]?.[metric] || 0))
      },
      data: items.map((r, i) => ({
        value: r[metric] || 0,
        itemStyle: { color: COLORS[i % COLORS.length] }
      }))
    }]
  }), [items, metric, maxVal, activeMetric]);

  const handleClick = (iso, displayName) => {
    setDrillTitle(`Country: ${displayName}`);
    setDrillExtra({ country: iso });
    setDrillOpen(true);
  };

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading country data</p>
    </Card>
  );

  return (
    <>
      <Card className="tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
        {/* Header */}
        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FiGlobe size={16} className="tw-text-blue-500" />
            <h3 className="tw-font-bold tw-text-gray-900">By Country</h3>
            <span className="tw-text-xs tw-text-gray-400">{items.length} countries</span>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            {/* Metric toggle */}
            <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
              {METRICS.map((opt, idx) => (
                <button key={opt.key} onClick={() => setMetric(opt.key)}
                  className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${idx > 0 ? 'tw-border-l tw-border-l-gray-200' : ''} ${metric === opt.key ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}>
                  {opt.label}
                </button>
              ))}
            </div>

            {/* View toggle */}
            <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
              <button onClick={() => setView('bar')}
                className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${view === 'bar' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}>
                <FiBarChart2 size={11} /> Chart
              </button>
              <button onClick={() => setView('table')}
                className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 tw-border-l tw-border-l-gray-200 ${view === 'table' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}>
                <FiList size={11} /> Table
              </button>
            </div>
          </div>
        </div>

        {/* Bar chart view */}
        {view === 'bar' && (
          <>
            <div className="tw-p-4 tw-max-h-80 tw-overflow-y-auto">
              {items.length === 0 ? (
                <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
              ) : (
                <ReactECharts
                  option={barOption}
                  style={{ height: Math.max(items.length * 32 + 8, 120) }}
                  notMerge
                  onEvents={{
                    click: (params) => {
                      const row = items[params.dataIndex];
                      if (row) handleClick(row.country, countryName(row.country));
                    }
                  }}
                />
              )}
            </div>
            <div className="tw-px-5 tw-py-2 tw-border-t tw-border-gray-100 tw-text-xs tw-text-gray-400 tw-text-center">
              Click any row to view detailed contracts
            </div>
          </>
        )}

        {/* Detail table view */}
        {view === 'table' && (
          <div className="tw-overflow-auto tw-max-h-[460px]">
            <table className="tw-w-full tw-text-xs tw-border-collapse">
              <thead>
                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10">
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-8">#</th>
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600 tw-min-w-32">Country</th>
                  <th
                    onClick={() => handleTableSort('contract_count')}
                    className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none"
                  >
                    Contracts{tableSort.key === 'contract_count' && <span className="tw-ml-1 tw-text-gray-400">{tableSort.dir === 'desc' ? '▼' : '▲'}</span>}
                  </th>
                  <th
                    onClick={() => handleTableSort('addendum_count')}
                    className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none"
                  >
                    Addendums{tableSort.key === 'addendum_count' && <span className="tw-ml-1 tw-text-gray-400">{tableSort.dir === 'desc' ? '▼' : '▲'}</span>}
                  </th>
                  <th
                    onClick={() => handleTableSort('total_value')}
                    className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none"
                  >
                    Total ${tableSort.key === 'total_value' && <span className="tw-ml-1 tw-text-gray-400">{tableSort.dir === 'desc' ? '▼' : '▲'}</span>}
                  </th>
                  <th
                    onClick={() => handleTableSort('contract_weight')}
                    className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none"
                  >
                    Weight (MT){tableSort.key === 'contract_weight' && <span className="tw-ml-1 tw-text-gray-400">{tableSort.dir === 'desc' ? '▼' : '▲'}</span>}
                  </th>
                  <th className="tw-px-3 tw-py-2"></th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => (
                  <tr
                    key={row.country}
                    onClick={() => handleClick(row.country, countryName(row.country))}
                    className={`tw-border-b tw-border-gray-100 tw-cursor-pointer ${i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/40'} hover:tw-bg-blue-50/60 tw-transition-colors`}
                  >
                    <td className="tw-px-3 tw-py-2 tw-text-gray-400 tw-font-mono">{i + 1}</td>
                    <td className="tw-px-3 tw-py-2">
                      <div className="tw-font-semibold tw-text-gray-800">{countryName(row.country)}</div>
                      <div className="tw-text-gray-400">{row.country}</div>
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-blue-700">{Number(row.contract_count || 0).toLocaleString()}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-amber-600">{Number(row.addendum_count || 0).toLocaleString()}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-emerald-700">{fmtM(row.total_value)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{fmtW(row.contract_weight)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-blue-400">↗</td>
                  </tr>
                ))}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot>
                  <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-gray-100 tw-font-bold">
                    <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs" colSpan={2}>Total ({tableRows.length} countries)</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{tableRows.reduce((s, r) => s + (r.contract_count || 0), 0).toLocaleString()}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600">{tableRows.reduce((s, r) => s + (r.addendum_count || 0), 0).toLocaleString()}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-700">{fmtM(tableRows.reduce((s, r) => s + (r.total_value || 0), 0))}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{fmtW(tableRows.reduce((s, r) => s + (r.contract_weight || 0), 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      <ContractDrillDownPanel
        open={drillOpen}
        title={drillTitle}
        baseParams={baseParams || {}}
        extraParams={drillExtra}
        onClose={() => setDrillOpen(false)}
      />
    </>
  );
};

ContractCountryChart.propTypes = {
  data:       PropTypes.array,
  loading:    PropTypes.bool,
  error:      PropTypes.any,
  baseParams: PropTypes.object
};
ContractCountryChart.defaultProps = { data: [], loading: false, error: null, baseParams: {} };

export default ContractCountryChart;
